import { useCallback, useEffect, useMemo, useState } from 'react'
import { api } from '../api'
import { useI18n } from '../i18n'
import type { BalanceRow } from '../types'
import { addDays, parseYmd, todayYmd, ymd } from '../lib/dates'
import { Avatar, Empty, Spinner, useToast } from '../components/ui'

const RANGES = [30, 90, 365] as const
type Range = (typeof RANGES)[number]

export default function Balance() {
  const { t } = useI18n()
  const toast = useToast()
  const [rows, setRows] = useState<BalanceRow[]>([])
  const [loading, setLoading] = useState(true)
  const [days, setDays] = useState<Range>(90)

  const load = useCallback(
    async (range: Range) => {
      setLoading(true)
      const today = parseYmd(todayYmd())
      const from = ymd(addDays(today, -range))
      const to = ymd(today)
      try {
        const r = await api.stats(from, to)
        setRows(r.rows)
      } catch {
        toast.push(t('balance.loadErr'), 'error')
      } finally {
        setLoading(false)
      }
    },
    [toast, t],
  )

  useEffect(() => {
    load(days)
  }, [load, days])

  const maxHours = useMemo(() => rows.reduce((m, r) => Math.max(m, r.hours), 0), [rows])

  return (
    <div>
      <div className="page-head between wrap">
        <div>
          <h1>{t('balance.title')}</h1>
          <p>{t('balance.subtitle')}</p>
        </div>
        <div className="segmented">
          {RANGES.map((r) => (
            <button key={r} className={days === r ? 'active' : ''} onClick={() => setDays(r)}>
              {t(`balance.range${r}`)}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="full-loader" style={{ minHeight: 240 }}>
          <Spinner large />
        </div>
      ) : rows.length === 0 ? (
        <div className="card">
          <Empty title={t('balance.empty')} />
        </div>
      ) : (
        <div className="list card">
          {rows.map((r) => {
            const pct = maxHours > 0 ? Math.round((r.hours / maxHours) * 100) : 0
            return (
              <div className="list-row" key={r.user_id}>
                <Avatar name={r.name} email={r.email} green sm />
                <div className="grow" style={{ minWidth: 0 }}>
                  <div className="title">{r.name || r.email}</div>
                  <div className="sub">
                    {t('balance.shifts', { n: r.shifts })} · {t('balance.hours', { n: r.hours })}
                  </div>
                  <div className="coverbar" style={{ marginTop: 6, maxWidth: 320 }}>
                    <span className="green" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
